import { removeExceptFirst } from "@sinch/utils/string";
import i18next from "i18next";
import { isEmpty, isNil } from "ramda";
import { isNilOrEmpty } from "ramda-adjunct";
import type { ReactNode } from "react";
import { SegmentedMessage } from "sms-segments-calculator";
import { addMethod, AnyObject, array, Flags, Maybe, number, object, Schema, string } from "yup";
import { SMS_MAX_LENGTH } from "@sinch/core/config/globals";

/**
 * Register custom validation in yup module for typescript
 */
declare module "yup" {
  interface NumberSchema<
    TType extends Maybe<number> = number | undefined,
    TContext = AnyObject,
    TDefault = undefined,
    TFlags extends Flags = "",
  > extends Schema<TType, TContext, TDefault, TFlags> {
    maxDecimals(decimals: number): NumberSchema<TType, TContext>;
  }
  interface StringSchema<
    TType extends Maybe<string> = string | undefined,
    TContext = AnyObject,
    TDefault = undefined,
    TFlags extends Flags = "",
  > extends Schema<TType, TContext, TDefault, TFlags> {
    smsMessage(placeholders?: { label: ReactNode; value: string; size: number }[]): StringSchema<TType, TContext>;
  }
}

/**
 * Validate field for number of decimal places
 */
addMethod(number, "maxDecimals", function format(decimals: number) {
  const patternTwoDigisAfterComma = new RegExp(`^-?\\d+(\\.\\d{0,${decimals}})?$`);
  return this.test(
    "is-decimal",
    () => i18next.t("maxDecimals", { decimals }),
    (val: any) => {
      if (val !== undefined && val !== "") {
        let inputValue = String(val).replace(/[^\d.-]/g, ""); // remove all non-numeric chars
        inputValue = removeExceptFirst(inputValue, "."); // remove other dots
        inputValue = inputValue.slice(0, 1) + inputValue.slice(1).replace("-", ""); // remove more than one minus char
        return isEmpty(inputValue) || patternTwoDigisAfterComma.test(inputValue); // test on decimals
      }
      return true;
    }
  );
});

/**
 * Validate field for number of decimal places
 */
addMethod(string, "smsMessage", function format(placeholders?: { label: ReactNode; value: string; size: number }[]) {
  return this.test({
    name: "max-sms-length",
    test: (value: string | undefined) => {
      if (isNil(value)) {
        return false;
      }
      const filledValue =
        placeholders?.reduce((acc, { value: val, size }) => acc.replace(`[[${val}]]`, "_".repeat(size)), value) ??
        value;
      const segmentedMessage = new SegmentedMessage(filledValue);
      const characterCount = segmentedMessage.encodedChars.reduce(
        (chars, encodedChar) => chars + encodedChar.codeUnits.length,
        0
      );
      return characterCount <= SMS_MAX_LENGTH;
    },
    message: i18next.t("inputMax", { max: SMS_MAX_LENGTH }),
  });
});

/**
 * Creates a Yup validation schema for MultilanguageTextInput values.
 *
 * @param requiredLanguage - The language code that must have a value (e.g., "en", "de")
 * @param errorMessage - Optional custom error message for the required language
 * @returns A Yup schema that validates the multilanguage input
 *
 * @example
 * ```tsx
 * import { multilanguageTextInputSchema } from "@sinch/utils/react-hook-form/validation";
 *
 * const schema = object({
 *   description: multilanguageTextInputSchema("en", "English description is required"),
 * });
 * ```
 */
export const multilanguageTextInputSchema = (props: { requiredLanguage?: string; errorMessage?: string }) => {
  const schema = array().of(
    object({
      language: string().required(),
      value: string().required(),
    })
  );
  if (props.requiredLanguage) {
    return schema
      .test(
        "required-language",
        props.errorMessage ??
          i18next.t("languageInputRequired", {
            requiredLanguage: props.requiredLanguage,
          }),
        (value, ctx) => {
          if (!value || !Array.isArray(value)) {
            return false;
          }

          const requiredLanguageEntry = value.find((item) => item.language === props.requiredLanguage);

          if (!requiredLanguageEntry || isNilOrEmpty(requiredLanguageEntry.value)) {
            return ctx.createError({
              path: ctx.path,
              message:
                props.errorMessage ??
                i18next.t("languageInputRequired", {
                  requiredLanguage: props.requiredLanguage,
                }),
            });
          }

          return true;
        }
      )
      .default([]);
  }
  return schema;
};
